import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal } from "lucide-react";

const RANGE_FIELDS = [
  { key: "da", label: "DA" },
  { key: "pa", label: "PA" },
  { key: "ss", label: "SS (%)" },
  { key: "backlinks", label: "Backlinks" },
];

const SORT_OPTIONS = [
  { value: "none", label: "Scan order" },
  { value: "da", label: "DA" },
  { value: "pa", label: "PA" },
  { value: "ss", label: "SS" },
  { value: "backlinks", label: "Backlinks" },
  { value: "ref_domains", label: "Ref. Domains" },
  { value: "domain", label: "Domain (A-Z)" },
];

export const FilterPopover = ({ filters, setFilters, sortKey, sortDir, setSortKey, setSortDir, activeCount, children }) => {
  const setRange = (key, bound, value) => {
    setFilters((f) => ({ ...f, [key]: { ...f[key], [bound]: value } }));
  };

  const reset = () => {
    setFilters({ da: { min: "", max: "" }, pa: { min: "", max: "" }, ss: { min: "", max: "" }, backlinks: { min: "", max: "" } });
    setSortKey(null);
    setSortDir("desc");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end" data-testid="filter-popover">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-gray-900 flex items-center gap-1.5 text-sm">
            <SlidersHorizontal size={14} className="text-[#C0392B]" /> Filter & Sort
          </p>
          <button data-testid="filter-reset-btn" onClick={reset} className="text-xs text-[#C0392B] hover:underline font-medium">
            Reset all
          </button>
        </div>

        <div className="space-y-3">
          {RANGE_FIELDS.map((f) => (
            <div key={f.key} className="grid grid-cols-3 items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{f.label}</span>
              <Input
                data-testid={`filter-${f.key}-min`}
                type="number"
                placeholder="Min"
                value={filters[f.key].min}
                onChange={(e) => setRange(f.key, "min", e.target.value)}
                className="h-8 text-sm"
              />
              <Input
                data-testid={`filter-${f.key}-max`}
                type="number"
                placeholder="Max"
                value={filters[f.key].max}
                onChange={(e) => setRange(f.key, "max", e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>

        <div className="border-t border-gray-100 mt-4 pt-4 space-y-3">
          <div className="grid grid-cols-3 items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort by</span>
            <div className="col-span-2">
              <Select value={sortKey || "none"} onValueChange={(v) => setSortKey(v === "none" ? null : v)}>
                <SelectTrigger data-testid="filter-sort-field" className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</span>
            <div className="col-span-2 flex bg-gray-100 p-0.5 rounded-full">
              {["asc", "desc"].map((d) => (
                <button
                  key={d}
                  data-testid={`filter-sort-${d}`}
                  onClick={() => setSortDir(d)}
                  className={`flex-1 text-xs py-1 rounded-full font-medium transition-colors ${
                    sortDir === d ? "bg-white text-[#C0392B] shadow-sm border border-[#C0392B]/30" : "text-gray-500"
                  }`}
                >
                  {d === "asc" ? "Ascending" : "Descending"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeCount > 0 && (
          <p className="mt-3 text-xs text-gray-400" data-testid="filter-active-count">{activeCount} active filter{activeCount > 1 ? "s" : ""}</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
